"use client";
import moveUp from "../../../public/icons/moveUp.svg";
import moveDown from "../../../public/icons/moveDown.svg";
import aiIntegrationIcon from "../../../public/ai-integration.png";
import caseStudyIcon from "../../../public/case-study.png";
import React, { useRef, useState } from "react";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import DownloadBrochure from "../modal/DownloadBrochure";
import {
  ContentContainer,
  H2Heading,
  H4Heading,
  Heading,
  Layout,
  Stack,
  SubHeading,
  TP,
} from "../molecules";
import { useDictionary } from "../../context/dictionaryProvider";
import Image from "next/image";

const parseCurriculumContent = (content = "") => {
  const lines = (typeof content === "string" ? content : "")
    .split("\n")
    .map((line) => line.trim());
  const introItems = [];
  const sections = [];
  let currentSection = null;

  const pushCurrentSection = () => {
    if (currentSection) {
      sections.push(currentSection);
      currentSection = null;
    }
  };

  lines.forEach((line) => {
    if (!line) return;

    if (line.startsWith("## ")) {
      pushCurrentSection();
      currentSection = {
        title: line.replace(/^##\s+/, "").trim(),
        bullets: [],
        paragraphs: [],
      };
      return;
    }

    const cleanedBullet = line.replace(/^[-*]\s+/, "").trim();
    const isBullet = /^[-*]\s+/.test(line);

    if (!currentSection) {
      if (isBullet) introItems.push(cleanedBullet);
      return;
    }

    if (isBullet) {
      currentSection.bullets.push(cleanedBullet);
    } else {
      currentSection.paragraphs.push(
        line
          .replace(/^\*\*(.*?)\*\*$/, "$1")
          .replace(/^\*\*(.*?)\*\*:(.*)$/, "$1:$2")
          .trim()
      );
    }
  });

  pushCurrentSection();

  return {
    introItems,
    sections,
    hasStructuredContent: introItems.length > 0 || sections.length > 0,
  };
};

const normalizeRichTextContent = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.trim();

  const getNodeText = (node) => {
    if (!node) return "";
    if (typeof node === "string") return node;
    if (typeof node.text === "string") return node.text;
    if (Array.isArray(node.children)) {
      return node.children.map(getNodeText).join("");
    }
    return "";
  };

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item;
        if (item?.type === "list" && Array.isArray(item.children)) {
          return item.children
            .map((child) => getNodeText(child).trim())
            .filter(Boolean)
            .map((text) => `- ${text}`)
            .join("\n");
        }
        if (item?.children && Array.isArray(item.children)) {
          return getNodeText(item);
        }
        return "";
      })
      .join("\n")
      .trim();
  }
  if (typeof value === "object") {
    if (typeof value.body === "string") return value.body.trim();
    if (typeof value.content === "string") return value.content.trim();
  }
  return "";
};

const normalizeAiIntegrationItems = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (!item || typeof item !== "object") return "";
      return (
        item?.title?.trim?.() ||
        item?.text?.trim?.() ||
        item?.content?.trim?.() ||
        item?.body?.trim?.() ||
        ""
      );
    })
    .filter(Boolean);
};

const AccordionItem = ({
  title,
  subheadings,
  aiIntegration,
  caseStudy,
  isOpen,
  onClick,
  index,
  faqs,
}) => {
  const contentRef = useRef(null);
  const parsedContent = parseCurriculumContent(subheadings);
  const aiIntegrationItems = normalizeAiIntegrationItems(aiIntegration);
  const caseStudyContent = normalizeRichTextContent(caseStudy);
  const hasCards = aiIntegrationItems.length > 0 || caseStudyContent;
  const introColumns = [
    parsedContent.introItems.slice(0, Math.ceil(parsedContent.introItems.length / 2)),
    parsedContent.introItems.slice(Math.ceil(parsedContent.introItems.length / 2)),
  ];

  return (
    <div
      id="curriculum"
      className="transform transition-all duration-1000 ease-linear text-start"
    >
      <button
        className={`flex justify-between items-center w-full py-7 px-6 lg:px-10 font-semibold text-base mb-2 text-start transition-colors
        ${isOpen ? "bg-[#316BCB] text-[#fff]" : "bg-[#FFDA73] text-[#316BCB]"}`}
        onClick={onClick}
        data-track={isOpen ? undefined : "curriculum_expand"}
        data-track-module-number={index + 1}
        data-track-module-name={title}
        aria-expanded={isOpen}
      >
        <h3 className="flex gap-3">
          <span>{index + 1}.</span>
          {title}
        </h3>
        {isOpen ? (
          <Image
            height={30}
            width={30}
            src={moveUp}
            alt="moveUp"
            loading="lazy"
          />
        ) : (
          <Image
            height={30}
            width={30}
            src={moveDown}
            alt="moveDown"
            loading="lazy"
          />
        )}
      </button>
      <div
        ref={contentRef}
        style={{
          maxHeight: isOpen ? `${contentRef.current.scrollHeight}px` : "0px",
        }}
        className={`overflow-hidden transition-[max-height] duration-500 ease-in-out ${
          isOpen ? "bg-[#316BCB] -mt-2 mb-2" : ""
        }`}
      >
        {parsedContent.hasStructuredContent ? (
          <div className="px-10 pt-6 pb-10 lg:px-14">
            {parsedContent.introItems.length > 0 && (
              <div className="mb-8 grid gap-4 lg:grid-cols-2 lg:gap-x-8">
                {introColumns.map((column, columnIndex) => (
                  <div key={columnIndex} className="space-y-3">
                    {column.map((item, itemIndex) => (
                      <div key={`${columnIndex}-${itemIndex}`} className="flex items-start gap-3 text-white/95">
                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-semibold">
                          {columnIndex * introColumns[0].length + itemIndex + 1}
                        </span>
                        <p className="text-base leading-7">{item}</p>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {hasCards && (
              <div className="grid gap-5 lg:grid-cols-2">
                {aiIntegrationItems.length > 0 && (
                  <div className="rounded-[24px] bg-[#316BCB] px-9 py-7 text-white">
                    <div className="mb-5 flex items-center gap-3">
                      <Image
                        src={caseStudyIcon}
                        alt="AI Integration"
                        width={32}
                        height={32}
                        className="h-8 w-8 shrink-0 object-contain"
                      />
                      <h4 className="text-[20px] font-bold leading-7">
                        AI Integration
                      </h4>
                    </div>
                    <ul className="space-y-2 pl-5 text-base leading-7 text-white/90 list-disc">
                      {aiIntegrationItems.map((item, itemIndex) => (
                        <li key={`${title}-ai-${itemIndex}`}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {caseStudyContent && (
                  <div className="rounded-[24px] bg-[#316BCB] px-9 py-7 text-white">
                    <div className="mb-5 flex items-center gap-3">
                      <Image
                        src={aiIntegrationIcon}
                        alt="Activities/Case Study"
                        width={32}
                        height={32}
                        className="h-8 w-8 shrink-0 object-contain"
                      />
                      <h4 className="text-[20px] font-bold leading-7">
                        Activities/Case Study
                      </h4>
                    </div>
                    <Markdown
                      className="text-base leading-7 text-white/90"
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        p: ({ children }) => (
                          <p className="mb-3 text-base leading-7 text-white/90">
                            {children}
                          </p>
                        ),
                        ul: ({ children }) => (
                          <ul className="space-y-2 pl-5 text-base leading-7 text-white/90 list-disc">
                            {children}
                          </ul>
                        ),
                        ol: ({ children }) => (
                          <ol className="space-y-2 pl-5 text-base leading-7 text-white/90 list-decimal">
                            {children}
                          </ol>
                        ),
                        li: ({ children }) => <li>{children}</li>,
                        strong: ({ children }) => (
                          <strong className="font-semibold text-white">{children}</strong>
                        ),
                        a: ({ children, ...rest }) => (
                          <a className="underline underline-offset-2 text-white" {...rest}>
                            {children}
                          </a>
                        ),
                      }}
                    >
                      {caseStudyContent}
                    </Markdown>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <Markdown
            className="curriculum-markdown px-6 pt-2 pb-10 lg:px-10 xl:px-16"
            rehypePlugins={[rehypeRaw]}
            components={{
              h1: ({ children }) => (
                <Heading className="mb-3 text-white">{children}</Heading>
              ),
              h2: ({ children }) => (
                <H2Heading className="mb-3 text-white">{children}</H2Heading>
              ),
              h3: ({ children }) => (
                <SubHeading className="mb-3 text-white">{children}</SubHeading>
              ),
              h4: ({ children }) => (
                <H4Heading className="mb-3 text-white">{children}</H4Heading>
              ),
              p: ({ children }) => (
                <TP className={`${faqs ? faqs : "my-3"} text-white/90`}>
                  {children}
                </TP>
              ),
              a: ({ children, ...rest }) => (
                <a
                  className="underline underline-offset-2 text-white hover:text-white/80"
                  {...rest}
                >
                  {children}
                </a>
              ),
              ul: ({ children }) => (
                <ul className="grid gap-3 text-white md:grid-cols-2">
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="grid gap-3 text-white md:grid-cols-2">
                  {children}
                </ol>
              ),
              li: ({ children }) => (
                <li className="flex items-start gap-3 rounded-[18px] bg-white/10 px-4 py-3 text-sm leading-6 text-white list-none">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-bold">
                    •
                  </span>
                  <span>{children}</span>
                </li>
              ),
            }}
          >
            {subheadings}
          </Markdown>
        )}
      </div>
    </div>
  );
};
const Curriculum = ({ course }) => {
  const dict = useDictionary();
  const [expandedIndex, setExpandedIndex] = useState(null);
  const handleToggle = (index) => {
    setExpandedIndex(index === expandedIndex ? null : index);
  };

  const validModules = (course?.course_modules || []).filter(
    (module) =>
      module?.heading ||
      module?.subheadings ||
      module?.ai_integration?.length ||
      module?.case_study
  );

  const accordianDataList = validModules.map((curri) => ({
    heading: curri?.heading,
    subheadings: curri?.subheadings,
    aiIntegration: curri?.ai_integration,
    caseStudy: curri?.case_study,
  }));

  if (accordianDataList.length === 0) return null;

  const curriculum = expandedIndex ? accordianDataList : accordianDataList;
  return (
    <Layout className="">
      <ContentContainer>
        <Stack
          className={`flex items-center gap-4 mx-auto mb-20`}
          style={{ flexDirection: "column" }}
        >
          <h2 className="text-[32px] lg:text-5xl font-extrabold text-black">
            {dict?.curriculum.title}
          </h2>
        </Stack>
        <div
          className={`overflow-hidden h-full transform transition-max-height ease-linear duration-500 
            ${
              expandedIndex
                ? "overflow-y-auto max-h-full ease-in"
                : "overflow-hidden max-h-full ease-out"
            }`}
        >
          {curriculum?.map((item, index) => (
            <React.Fragment key={index}>
              <AccordionItem
                aria-label={item?.heading}
                title={item?.heading}
                subheadings={item?.subheadings}
                aiIntegration={item?.aiIntegration}
                caseStudy={item?.caseStudy}
                isOpen={index === expandedIndex}
                index={index}
                onClick={() => handleToggle(index)}
              />
            </React.Fragment>
          ))}
        </div>
        <div className="flex items-center justify-center w-full mt-8">
          <DownloadBrochure document={course?.brochure} course={course} submitEventName="Download brochure footer" closeEventName="Download brochure footer X" />
        </div>
      </ContentContainer>
    </Layout>
  );
};

export default Curriculum;
